/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  combineReducers,
  composeReducers,
  composeSetters,
  EntityConfig,
  entityStateMerger,
  entityValidatorByEntityConfig,
  errorsEntityState,
  fieldValidationSetter,
  fieldValidatorByEntityConfig,
  getInvalidFields,
  mergeEntitiesBy,
  MutateStateEvent,
  NumberTypes,
  removeFromArrayReducer,
  setDirty,
  setEntityField,
  setFieldError,
  setRecalculationNeeded,
  updateInArrayReducer,
} from '@gh/common-shared';
import { getEntityFieldState } from '@gh/common-shared-data';
import { FocusedCell } from '@gh/core-ui-grid';
import { constant, find, groupBy, pickBy } from 'lodash';
import {
  ComponentItem,
  whenComponentItemInvalid,
} from '../../resources/component-and-item-details.model';

export interface ComponentDetailsItemsState {
  items: ComponentItem[];
  focusedCell?: FocusedCell;
  isDirty: boolean;
  isRecalculationNeeded: boolean;
}

// all possible mutate events
export enum ComponentDetailsItemsStateEvent {
  Load, Merge, MarkAsPristine, SetItemField, RemoveItem, AddItems, NavigateToInvalid,
}

// initial state of items tab
export const COMPONENT_DETAILS_ITEMS_INIT_STATE: ComponentDetailsItemsState = {
  items: [],
  focusedCell: void 0,
  isDirty: false,
  isRecalculationNeeded: false,
};

// state of fields which is important for editable grid
const FIELD_STATES = {
  costCenter: { editable: true, required: true },
  quantity: { editable: true, required: true },
};

// editable field validators
const COMPONENT_ITEM_ENTITY_CONFIG: EntityConfig<ComponentItem> = {
  costCenter: { required: true },
  quantity: { required: true, type: NumberTypes.SignedQuantity },
};

// builds reducer for { items, focusedCell } structure
// and merge it with other reducers to update global flags and navigate to invalid cell
const effectReducer = composeReducers(
  combineReducers({
    items: itemsReducer,
  }),
  globalFlagsReducer,
  invalidReducer,
);

// by field validator
export const validateItem = entityValidatorByEntityConfig(COMPONENT_ITEM_ENTITY_CONFIG);
// whole entity validator
export const validateItemField = fieldValidatorByEntityConfig(COMPONENT_ITEM_ENTITY_CONFIG);

// this function will complement each item to contain entityState
// entity state holds information related to editable fields
export const complementItem = entityStateMerger<ComponentItem, any>(
  constant(FIELD_STATES),
  errorsEntityState(validateItem),
);

// returns entity state of ComponentItem
export const getComponentItemState = getEntityFieldState;

// returns id of the componentItem
// we use generated id here, because item can have several items having the same itemId, but different cost centers
export const getComponentItemId = (item: ComponentItem) => item.uniqueId;

const byComponentItemId = (event: MutateStateEvent, item: ComponentItem) => getComponentItemId(item) === event.id;
const getComponentItemDBPrimaryKey = ({ itemId, costCenter }: ComponentItem) =>
  `${itemId}-${costCenter && costCenter.id}`;

const mergeComponentItems = mergeEntitiesBy(
  getComponentItemDBPrimaryKey,
  (dest, source) => ({ ...dest, isDirty: source.isDirty }));

// complex setter:
// 1. sets corresponding entity field,
// 2. sets dirty flag if and only if previous method modified value and dirty was not set before
// 3. stores all validation errors in entity state, if any
const setItemField = composeSetters(
  setEntityField,
  setDirty,
  fieldValidationSetter(validateItemField));

// nothing interesting, delegates set operation to the setItemField
const itemReducer = (item: ComponentItem, event: MutateStateEvent) => setItemField(item, event.name, event.value);

// 1. updates item (satisfying given predicate) in the array by the reducer
// 2. validates that tuples of itemId and costCenters are unique
const itemsUpdateReducer = composeReducers(
  updateInArrayReducer(itemReducer, byComponentItemId),
  itemsCostCenterReducer,
);

// 1. removes updates item (satisfying given predicate) from the array
// 2. validates that tuples of itemId and costCenters are unique
const itemsRemoveReducer = composeReducers(
  removeFromArrayReducer(byComponentItemId),
  itemsCostCenterReducer,
);

/**
 * Root reducer for this state
 */
export function componentDetailsReducer(state: ComponentDetailsItemsState,
                                        event: MutateStateEvent): ComponentDetailsItemsState {
  switch (event.type) {
    case ComponentDetailsItemsStateEvent.SetItemField:
    case ComponentDetailsItemsStateEvent.RemoveItem:
    case ComponentDetailsItemsStateEvent.AddItems:
    case ComponentDetailsItemsStateEvent.NavigateToInvalid:
      // all grid related events
      return effectReducer(state, event);
    case ComponentDetailsItemsStateEvent.Load:
      // initialization of state
      return {
        ...state,
        items: event.data.items.map(complementItem),
        isRecalculationNeeded: false,
        isDirty: false,
      };
    case ComponentDetailsItemsStateEvent.Merge:
      // initialization of state
      return {
        ...state,
        items: mergeComponentItems(event.data.items.map(complementItem), state.items),
        isRecalculationNeeded: false,
      };
    case ComponentDetailsItemsStateEvent.MarkAsPristine:
      return {
        ...state,
        isDirty: false,
      };
    default:
      return state;
  }
}

/**
 * Updates global flags, like recalculationNeeded and dirty
 */
function globalFlagsReducer(state: ComponentDetailsItemsState, event: MutateStateEvent): ComponentDetailsItemsState {
  switch (event.type) {
    case ComponentDetailsItemsStateEvent.RemoveItem:
    case ComponentDetailsItemsStateEvent.AddItems:
      return setDirty(setRecalculationNeeded(state));
    case ComponentDetailsItemsStateEvent.SetItemField:
      if (event.name === 'quantity') {
        return setDirty(setRecalculationNeeded(state));
      } else {
        return setDirty(state);
      }
    default:
      return state;
  }
}

/**
 * Updates state to point user to invalid cell in grid
 */
function invalidReducer(state: ComponentDetailsItemsState, event: MutateStateEvent): ComponentDetailsItemsState {
  if (event.type !== ComponentDetailsItemsStateEvent.NavigateToInvalid) {
    return state;
  }

  const item = find<ComponentItem>(state.items, whenComponentItemInvalid);

  if (item) {
    const fields = getInvalidFields(item);

    return {
      ...state,
      focusedCell: {
        id: getComponentItemId(item),
        field: fields[0],
      },
    };
  } else {
    return state;
  }
}

/**
 * Update list of items
 */
function itemsReducer(items: ComponentItem[], event: MutateStateEvent): ComponentItem[] {
  switch (event.type) {
    case ComponentDetailsItemsStateEvent.SetItemField:
      // updates separate element
      return itemsUpdateReducer(items, event);
    case ComponentDetailsItemsStateEvent.RemoveItem:
      // removes element from the array
      return itemsRemoveReducer(items, event);
    case ComponentDetailsItemsStateEvent.AddItems:
      // adds element into the array
      return event.items.map(complementItem).map(setDirty).concat(items);
    default:
      return items;
  }
}

/**
 * Validates if array of items has item having the same id and cost center.
 * If it is the case all such items are marked by error
 */
function itemsCostCenterReducer(items: ComponentItem[], event: MutateStateEvent): ComponentItem[] {
  switch (event.type) {
    case ComponentDetailsItemsStateEvent.SetItemField:
      if (event.name === 'costCenter') {
        return validateItemsCostCenter(items);
      } else {
        return items;
      }
    case ComponentDetailsItemsStateEvent.RemoveItem:
      return validateItemsCostCenter(items);
    default:
      return items;
  }
}

/**
 * Validates if array of items has item having the same id and cost center.
 *
 * Implementation of this method does not look very performant
 * @param items
 */
function validateItemsCostCenter(items: ComponentItem[]): ComponentItem[] {
  // group all items by DB key
  const itemsById = groupBy(items, getComponentItemDBPrimaryKey);
  // if we have several items for one db key, then they use the same cost center
  const itemsHavingSameId = pickBy(itemsById, (i) => i.length >= 2);
  // update error state of costCenter field
  return items.map((item) => setFieldError(
    item,
    'costCenter',
    'exists',
    itemsHavingSameId[getComponentItemDBPrimaryKey(item)] ? { messageId: 'lbl_item_cc_exist' } : void 0));
}
